/**
 * Client helpers for the Salesforce lookup typeahead endpoints used by
 * the Create Plan modal.
 *
 * Each search call is a single GET against the Snowflake mirror of the
 * matching SF object (Advertiser__c, Brands__c, Contact). Responses are
 * shaped as small `{id, name}` arrays (Contact additionally carries
 * its parent Account so the modal's Agency line auto-fills).
 *
 * `resolveSalesPlan` is the bulk-resolve helper for roll-forward —
 * given a 25/26 SF SALES_PLAN id, returns the four (id, name) pairs
 * already on that plan so we can pre-populate every LookupField
 * without a per-field fetch.
 *
 * No caching: lookup volumes are small per query (LIMIT 20 server-
 * side) and the network round-trip is debounced on the input side,
 * so an in-memory cache would add complexity without paying for
 * itself.
 */

import { authAxios } from './session_utils';

const API_BASE = process.env.REACT_APP_BACK_END_API;

// ─── Types ───────────────────────────────────────────────────────────

/** Minimal {id, name} shape returned by Advertiser / Brand searches. */
export interface LookupItem {
  id: string;
  name: string;
}

/**
 * Contact search response — includes the parent Account so the modal
 * can show the derived Agency name as soon as the user picks a Contact,
 * without firing another fetch.
 */
export interface ContactLookupItem extends LookupItem {
  accountId: string | null;
  accountName: string | null;
}

/**
 * Brand search response — includes the parent Advertiser so the modal
 * can auto-fill Advertiser when the user picks a Brand without picking
 * an Advertiser first (the brand-first flow). Always populated because
 * the backend JOINs to Advertiser__c regardless of scope.
 */
export interface BrandLookupItem extends LookupItem {
  advertiserId: string | null;
  advertiserName: string | null;
}

/**
 * Roll-forward pre-population payload. Each field is null if the source
 * SF plan didn't have that lookup set — the matching LookupField in
 * the modal just renders empty in that case.
 */
export interface ResolvedSalesPlan {
  advertiser: LookupItem | null;
  brand: LookupItem | null;
  contact: LookupItem | null;
  account: LookupItem | null;
}

// ─── Result ranking ──────────────────────────────────────────────────
//
// The SF-mirror endpoints do a substring match, so a query like "AT&T"
// returns "AT&T Inc" alongside "z& AT&T" (which just happens to contain
// the letters "AT&T" somewhere in its name). That's fine as recall, but
// visually the user expects the prefix match on top.
//
// Re-rank on the client (results are already capped at ~20, so sort cost
// is negligible): exact match → starts-with → word-boundary → substring.
// Ties break alphabetically so the ordering is stable across renders.
function matchRank(hay: string, needle: string): number {
  if (hay === needle) return 0;
  if (hay.startsWith(needle)) return 1;
  // Word-boundary check — "wireless" ranks above "cellular wireless plan"
  // ahead of "somewordwireless". Escape the needle so special regex chars
  // (& . +) don't break the pattern.
  const escaped = needle.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  if (new RegExp(`\\b${escaped}`).test(hay)) return 2;
  return 3;
}
function rankResults<T extends { name: string }>(items: T[], q: string): T[] {
  const needle = q.trim().toLowerCase();
  if (!needle) return items;
  return [...items].sort((a, b) => {
    const ra = matchRank(a.name.toLowerCase(), needle);
    const rb = matchRank(b.name.toLowerCase(), needle);
    if (ra !== rb) return ra - rb;
    return a.name.localeCompare(b.name);
  });
}

// ─── Fetchers ────────────────────────────────────────────────────────

/**
 * Search Advertiser__c by name (substring, case-insensitive, top 20).
 * Empty `q` is allowed — backend returns the first 20 alphabetically.
 */
export async function searchAdvertisers(q: string): Promise<LookupItem[]> {
  try {
    const response = await authAxios({
      method: 'GET',
      url: `${API_BASE}/snowflake/sf-lookup/advertiser`,
      params: { q },
    });
    return rankResults((response?.data?.advertisers ?? []) as LookupItem[], q);
  } catch {
    // Swallow network errors here — the typeahead is best-effort. The
    // dropdown will render "No matches" and the form can still be saved
    // (though the lookup field will be required-but-empty).
    return [];
  }
}

/**
 * Search Brands__c. Two modes:
 *
 *   • `advertiserId` provided  →  filter to that advertiser's brands
 *     (the modal's default once an Advertiser is picked — keeps the
 *     dropdown short).
 *   • `advertiserId` null      →  search ALL brands (~21k). Powers the
 *     brand-first flow: the user can type a brand without picking an
 *     Advertiser first, and the modal auto-fills Advertiser from the
 *     brand's parent on commit.
 *
 * Every result carries the parent advertiser's id + name, so the
 * caller's `onChange` handler can set both Brand and Advertiser state
 * from a single response.
 */
export async function searchBrands(
  q: string,
  advertiserId: string | null,
): Promise<BrandLookupItem[]> {
  try {
    const response = await authAxios({
      method: 'GET',
      url: `${API_BASE}/snowflake/sf-lookup/brand`,
      params: advertiserId ? { q, advertiserId } : { q },
    });
    return rankResults((response?.data?.brands ?? []) as BrandLookupItem[], q);
  } catch {
    return [];
  }
}

/**
 * Search Contact, returns the parent Account info too. When the modal
 * commits a Contact pick, it stashes accountId + accountName from this
 * payload into the form state to drive the read-only Agency line.
 */
export async function searchContacts(q: string): Promise<ContactLookupItem[]> {
  try {
    const response = await authAxios({
      method: 'GET',
      url: `${API_BASE}/snowflake/sf-lookup/contact`,
      params: { q },
    });
    return rankResults((response?.data?.contacts ?? []) as ContactLookupItem[], q);
  } catch {
    return [];
  }
}

/**
 * Fetch the full list of active SF Programs__c. Called once on MPB
 * mount so + Add Row / + Add Show can stamp the SF Program ID on new
 * PLAN_DEAL rows. Without it, new rows save with PROGRAM_ID = NULL and
 * the push-to-SF flow errors with MISSING_PROGRAM_IDS on brand-new
 * plans (no rolled-forward source deals to inherit an ID from).
 *
 * List is small (~dozens), no query param — the client builds a
 * name → id map in-process and looks up at row-creation time.
 *
 * Returns [] on error; the picker still works but new rows keep
 * `programId: null` and the pre-push validator surfaces which programs
 * need re-picking (existing behavior — no regression).
 */
export async function listPrograms(): Promise<LookupItem[]> {
  try {
    const response = await authAxios({
      method: 'GET',
      url: `${API_BASE}/snowflake/sf-lookup/programs`,
    });
    return (response?.data?.programs ?? []) as LookupItem[];
  } catch {
    return [];
  }
}

/**
 * Fetch the active picklist values for Sales_Plan__c.Primary_Demographic__c
 * from the backend, which in turn asks Salesforce via describe API.
 * Backend caches for 1 hour, so multiple frontend tabs share one hit
 * per hour.
 *
 * Returns [] on error → frontend falls back to the hardcoded
 * SYSTEM_DEMOS shortlist so the picker never breaks on an SF outage.
 */
export async function listPrimaryDemos(): Promise<string[]> {
  try {
    const response = await authAxios({
      method: 'GET',
      url: `${API_BASE}/snowflake/sf-lookup/primary-demos`,
    });
    return (response?.data?.demos ?? []) as string[];
  } catch {
    return [];
  }
}

/**
 * The Salesforce org this environment's backend pushes to, e.g.
 * `https://mvms.my.salesforce.com` or `https://mvms--training.sandbox.my.salesforce.com`.
 * Used to build "View in Salesforce" links.
 *
 * This is fetched rather than hardcoded because the frontend is a single
 * build serving both environments — it cannot know the target org at compile
 * time. The base was previously hardcoded to the Training sandbox, so once
 * production pushes went live every pushed plan produced a link with a real
 * PRODUCTION record id on the TRAINING host.
 *
 * Returns '' on error, which callers treat as "hide the link" — a missing
 * link is better than one that silently points at the wrong org.
 */
export async function getSfInstanceUrl(): Promise<string> {
  try {
    const response = await authAxios({
      method: 'GET',
      url: `${API_BASE}/snowflake/sf-lookup/instance-url`,
    });
    return ((response?.data?.instanceUrl ?? '') as string).replace(/\/$/, '');
  } catch {
    return '';
  }
}

/**
 * Bulk-resolve all four lookups for a 25/26 SF source plan. Called once
 * when the user picks a roll-forward source in the New Plan modal. A
 * single round-trip pre-fills every LookupField — no per-field fetch.
 *
 * Returns null if the SF plan id wasn't found (deleted, bad id) so the
 * modal can fall back to all-empty lookups.
 */
export async function resolveSalesPlan(
  salesPlanId: string,
): Promise<ResolvedSalesPlan | null> {
  if (!salesPlanId) return null;
  try {
    const response = await authAxios({
      method: 'GET',
      url: `${API_BASE}/snowflake/sf-lookup/resolve-sales-plan/${encodeURIComponent(salesPlanId)}`,
    });
    return (response?.data?.resolved ?? null) as ResolvedSalesPlan | null;
  } catch {
    return null;
  }
}
